'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Metric {
  id: number;
  label: string;
  value: string;
  unit: string;
  icon: string;
  trend: 'up' | 'down' | 'stable';
  trendValue: string;
  color: string;
}

const LiveMetrics = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metrics: Metric[] = [
    {
      id: 1,
      label: "Active Shipments",
      value: "247",
      unit: "loads",
      icon: "TruckIcon",
      trend: "up",
      trendValue: "+12%",
      color: "from-golden-light to-golden-mid"
    },
    {
      id: 2,
      label: "On-Time Delivery",
      value: "98.7",
      unit: "%",
      icon: "ClockIcon",
      trend: "up",
      trendValue: "+2.3%",
      color: "from-emerald-400 to-emerald-600"
    },
    {
      id: 3,
      label: "Safety Score",
      value: "99.2",
      unit: "%",
      icon: "ShieldCheckIcon",
      trend: "stable",
      trendValue: "0.0%",
      color: "from-blue-400 to-blue-600"
    },
    {
      id: 4,
      label: "Countries Served",
      value: "6",
      unit: "active",
      icon: "MapIcon",
      trend: "stable",
      trendValue: "0",
      color: "from-purple-400 to-purple-600"
    }
  ];

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return 'ArrowTrendingUpIcon';
    if (trend === 'down') return 'ArrowTrendingDownIcon';
    return 'MinusIcon';
  };

  const getTrendColor = (trend: string) => {
    if (trend === 'up') return 'text-emerald-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-gray-500';
  };

  return (
    <section className="py-12 bg-gradient-to-b from-background to-muted">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-golden-light/10 via-golden-mid/10 to-golden-dark/10 rounded-full mb-4">
            <Icon name="ChartBarIcon" size={20} className="text-golden-mid" />
            <span className="font-cta font-semibold text-sm text-primary">Live Operations Dashboard</span>
          </div>
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-3">
            Real-Time Performance Metrics
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            Track our operational excellence across Africa with live data updates
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric) => (
            <div
              key={metric.id}
              className="relative bg-card rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-border overflow-hidden group"
            >
              <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${metric.color} opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-300`} />
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br ${metric.color} rounded-lg shadow-md`}>
                    <Icon name={metric.icon as any} size={24} className="text-white" />
                  </div>
                  
                  {isHydrated && (
                    <div className={`flex items-center space-x-1 ${getTrendColor(metric.trend)}`}>
                      <Icon name={getTrendIcon(metric.trend) as any} size={16} />
                      <span className="font-mono text-xs font-semibold">{metric.trendValue}</span>
                    </div>
                  )}
                </div>

                <div className="mb-2">
                  <div className="flex items-baseline space-x-1">
                    <span className="font-headline font-bold text-4xl text-foreground">
                      {metric.value}
                    </span>
                    <span className="font-body text-sm text-muted-foreground">
                      {metric.unit}
                    </span>
                  </div>
                </div>

                <p className="font-body text-sm text-muted-foreground">
                  {metric.label}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="font-body text-sm text-muted-foreground">
            <Icon name="ArrowPathIcon" size={16} className="inline mr-1" />
            Data updated in real-time • Last sync: {isHydrated ? 'Just now' : 'Loading...'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default LiveMetrics;